import { useCallback, useMemo, useState } from "react"
import { Combobox, type ComboboxProps } from "./Combobox"
import { type ItemProps } from "./searchableList/SearchableList"

export const ComboboxWithPinnedItems = ({
  selectedIds,
  onChangeSelectedIds,
  items,
  ...props
}: Omit<
  ComboboxProps,
  "children" | "selectionMode" | "selectedId" | "onChangeSelectedId"
> & {
  items: ItemProps[]
  trigger?: React.ReactNode
  triggerRef?: React.RefObject<HTMLButtonElement>
}) => {
  const [tempSelectedItemIds, setTempSelectedItemIds] = useState<
    string[] | null | undefined
  >(selectedIds)

  const selectedItems = useMemo(() => {
    return items
      .filter((item) => tempSelectedItemIds?.includes(item.id))
      .map((item) => (
        <Combobox.Item
          key={item.id}
          id={item.id}
          value={item.value}
          keywords={item.keywords}
          onClick={item?.onClick}
        >
          {item.children}
          <Combobox.SelectionIndicator />
        </Combobox.Item>
      ))
  }, [tempSelectedItemIds, items])

  const filteredItems = useMemo(() => {
    return items
      .filter((item) => !tempSelectedItemIds?.includes(item.id))
      .map((item) => (
        <Combobox.Item
          key={item.id}
          id={item.id}
          value={item.value}
          keywords={item.keywords}
          onClick={item?.onClick}
          disabled={item.disabled}
        >
          {item.children}
          <Combobox.SelectionIndicator />
        </Combobox.Item>
      ))
  }, [tempSelectedItemIds, items])

  const handleOnOpenChange = useCallback(() => {
    setTempSelectedItemIds(selectedIds)
  }, [setTempSelectedItemIds, selectedIds])

  const children = useMemo(() => {
    return [
      ...selectedItems,
      selectedItems.length > 0 && <Combobox.Divider key="divider" />,
      ...filteredItems,
    ]
  }, [selectedItems, filteredItems])

  return (
    <Combobox
      selectionMode="multiple"
      selectedIds={selectedIds}
      onChangeSelectedIds={onChangeSelectedIds}
      closeOnSelect={false}
      onOpenChange={handleOnOpenChange}
      {...props}
    >
      {children}
    </Combobox>
  )
}
